import { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { getSalons, getActivityCategories } from '../../services/supabaseDb'
import type { SalonListItem, ActivityCategory } from '../../services/supabaseDb'
import { LoadingSpinner } from '../../components/common/LoadingSpinner'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { SaloniPublicHeader } from '../../components/common/SaloniPublicHeader'
import { PublicSiteFooter } from '../../components/common/PublicSiteFooter'
import { useAsyncData } from '../../hooks/useAsyncData'
import { Search, MapPin, Star, ChevronLeft, ChevronRight } from 'lucide-react'

const CARDS_PER_PAGE = 16

function filterSalons(
  salons: SalonListItem[],
  searchQuery: string,
  locationQuery: string
): SalonListItem[] {
  let result = salons
  if (searchQuery.trim()) {
    const q = searchQuery.trim().toLowerCase()
    result = result.filter(
      (s) =>
        (s.businessName ?? '').toLowerCase().includes(q) ||
        (s.address ?? '').toLowerCase().includes(q)
    )
  }
  if (locationQuery.trim()) {
    const loc = locationQuery.trim().toLowerCase()
    result = result.filter((s) => (s.address ?? '').toLowerCase().includes(loc))
  }
  return result
}

type SalonZoekData = {
  categories: ActivityCategory[]
  salons: SalonListItem[]
}

export function SalonZoekpagina() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [locationQuery, setLocationQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  const { data, loading, error, reload } = useAsyncData<SalonZoekData>(
    () =>
      Promise.all([
        getActivityCategories(),
        getSalons({ activityCategoryId: selectedCategory ?? undefined }),
      ]).then(([categories, salons]) => ({ categories, salons })),
    [selectedCategory]
  )

  const categories = data?.categories ?? []
  const salons = data?.salons ?? []

  const filteredSalons = useMemo(
    () => filterSalons(salons, searchQuery, locationQuery),
    [salons, searchQuery, locationQuery]
  )

  const totalPages = Math.max(1, Math.ceil(filteredSalons.length / CARDS_PER_PAGE))
  const paginatedSalons = useMemo(
    () =>
      filteredSalons.slice(
        (currentPage - 1) * CARDS_PER_PAGE,
        currentPage * CARDS_PER_PAGE
      ),
    [filteredSalons, currentPage]
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [selectedCategory, searchQuery, locationQuery])

  const getCategoryName = (salon: SalonListItem): string => {
    const firstId = salon.activityCategoryIds[0]
    if (!firstId) return ''
    return categories.find((c) => c.id === firstId)?.naam ?? ''
  }

  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      <SaloniPublicHeader />

      <main className="flex-1">
      {/* Hero + zoekbalk */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-24">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
          <p className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
            Welkom bij
          </p>
          <h1
            className="mt-2 text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl"
          >
            Guap<span className="text-primary">aa</span>
          </h1>
          <p className="mt-5 text-base text-foreground/90 sm:text-lg">
            Vind een salon bij jou in de buurt en maak direct een afspraak
          </p>

          {/* Zoekbalk + losse ronde zoekknop (geen border-probleem meer) */}
          <div className="mt-10 flex items-center gap-3">
            {/* Input-container: pil op desktop, kaart op mobiel */}
            <div className="flex flex-1 flex-col overflow-hidden rounded-2xl border border-border/80 bg-card shadow-md sm:flex-row sm:rounded-full sm:shadow-lg">
              <div className="flex min-w-0 flex-[1.4] items-center border-b border-border/60 py-4 pl-5 pr-4 sm:border-b-0 sm:border-r sm:pl-6 sm:pr-5">
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <input
                  type="search"
                  placeholder="Alle diensten en salons"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="ml-3 min-w-0 flex-1 bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none"
                  aria-label="Alle diensten en salons"
                />
              </div>
              <div className="flex min-w-0 flex-1 items-center py-4 pl-5 pr-4 sm:pl-6 sm:pr-5">
                <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <input
                  type="text"
                  placeholder="Huidige locatie"
                  value={locationQuery}
                  onChange={(e) => setLocationQuery(e.target.value)}
                  className="ml-3 min-w-0 flex-1 bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none"
                  aria-label="Huidige locatie"
                />
              </div>
            </div>
            {/* Ronde zoekknop – volledig los, geen border-clipping */}
            <button
              type="button"
              className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              aria-label="Zoeken"
            >
              <Search className="h-5 w-5" aria-hidden />
            </button>
          </div>

          {/* Categorie-filters onder de balk */}
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => setSelectedCategory(null)}
              className={`rounded-full px-4 py-2.5 text-sm font-medium transition-colors ${
                selectedCategory === null
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-secondary/80 text-foreground hover:bg-secondary'
              }`}
            >
              Alle
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() =>
                  setSelectedCategory(selectedCategory === c.id ? null : c.id)
                }
                className={`rounded-full px-4 py-2.5 text-sm font-medium transition-colors ${
                  selectedCategory === c.id
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-secondary/80 text-foreground hover:bg-secondary'
                }`}
              >
                {c.naam}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Salonkaarten – max 16 per pagina + paginatie */}
      <section className="mx-auto max-w-6xl px-4 pb-16 pt-10 sm:px-6">
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <ErrorMessage message={error} onRetry={reload} />
        ) : filteredSalons.length === 0 ? (
          <div className="rounded-2xl border border-border/80 bg-card p-12 text-center">
            <Search className="mx-auto h-12 w-12 text-muted-foreground/70" />
            <p className="mt-4 text-muted-foreground">
              Geen salons gevonden
              {(selectedCategory || searchQuery.trim() || locationQuery.trim()) &&
                ' met deze filters'}
              .
            </p>
            <button
              type="button"
              onClick={() => {
                setSelectedCategory(null)
                setSearchQuery('')
                setLocationQuery('')
              }}
              className="mt-4 text-sm font-medium text-primary hover:underline"
            >
              Filters wissen
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {paginatedSalons.map((s) => (
                <Link
                  key={s.id}
                  to={s.slug ? `/salon/${s.slug}` : '#'}
                  className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm transition hover:border-primary/40 hover:shadow-md"
                >
                  <div className="aspect-[4/3] w-full shrink-0 overflow-hidden bg-muted">
                    {s.bannerImageUrl ? (
                      <img
                        src={s.bannerImageUrl}
                        alt=""
                        className="h-full w-full object-cover transition group-hover:scale-[1.02]"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                        <span className="text-4xl font-sans text-primary/30">
                          {(s.businessName || 'S').charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-4">
                    <h2 className="font-semibold text-foreground">
                      {s.businessName || 'Salon'}
                    </h2>
                    {s.address && (
                      <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5 shrink-0" />
                        <span className="line-clamp-1">{s.address}</span>
                      </p>
                    )}
                    {getCategoryName(s) && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        {getCategoryName(s)}
                      </p>
                    )}
                    <div className="mt-2 flex items-center gap-1 text-sm text-muted-foreground">
                      <Star className="h-4 w-4 fill-amber-400 text-amber-400" aria-hidden />
                      <span>—</span>
                    </div>
                    <span className="mt-3 text-sm font-medium text-primary">
                      Boeken →
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            {totalPages > 1 && (
              <nav
                className="mt-10 flex flex-wrap items-center justify-center gap-2"
                aria-label="Paginatie"
              >
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="inline-flex items-center gap-1 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
                  aria-label="Vorige pagina"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Vorige
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        className={`min-w-[2.25rem] rounded-lg px-2 py-2 text-sm font-medium transition ${
                          page === currentPage
                            ? 'bg-primary text-primary-foreground'
                            : 'border border-border bg-card text-foreground hover:bg-muted'
                        }`}
                        aria-label={`Pagina ${page}`}
                        aria-current={page === currentPage ? 'page' : undefined}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage >= totalPages}
                  className="inline-flex items-center gap-1 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
                  aria-label="Volgende pagina"
                >
                  Volgende
                  <ChevronRight className="h-4 w-4" />
                </button>
              </nav>
            )}
          </>
        )}
      </section>
      </main>

      <PublicSiteFooter className="shrink-0" />
    </div>
  )
}
